import React, { useState, useRef, useEffect,useCallback, useMemo,useLayoutEffect } from "react";
import {
  Table,
  Input,
  Button,
  Space,
  Row,
  Col,
  Card,
  Breadcrumb,
  Badge,
  Empty,
  message,
  Spin
} from "antd";
import {
  PlusOutlined,
  HomeOutlined,
  SearchOutlined,
  EditOutlined,
  DownloadOutlined
} from "@ant-design/icons";
import { Resizable } from 'react-resizable';
import Create from "./Create";
import Edit from "./Edit";
import { useOutletContext } from 'react-router-dom';
import { useSelector } from 'react-redux';
import TemplatesAPI from '../../../restAPI/TemplatesAPI';
import moment from 'moment';
import { decodeToken } from "react-jwt";
const ResizableTitle = (props) => {
  const { onResize, width, children, ...restProps } = props;
  if (!width) return <th {...restProps}>{children}</th>;

  return (
    <th {...restProps}>
      <Resizable
        width={width}
        height={0}
        onResize={onResize}
        draggableOpts={{ enableUserSelectHack: false }}
        handle={
          <span
            style={{
              position: "absolute",
              right: 0,
              bottom: 0,
              cursor: "col-resize",
              width: "2px",
              height: "100%",
              background: "#ffeb3b3b",
            }}
          />
        }
      >
        <div>{children}</div>
      </Resizable>
    </th>
  );
};

export default function UserTable() {
  const tableContainerRef = useRef();
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const searchInput = useRef(null);
  const createRef = useRef(null);
  const editRef = useRef(null); 
  const { groupId, companyId } = useOutletContext();
  const { token } = useSelector(state => state.auth);
  const [templates, setTemplates] = useState([]);
  const [templatesForDisplay, setTemplatesForDisplay] = useState([]);
  const [downloadingTemplateId, setDownloadingTemplateId] = useState(null);

  // Open the Drawer in the Create component
  const showChildDrawer = () => {
    if (createRef.current) {
      createRef.current.openDrawer(); // Call the openDrawer method in Create
    }
  };

  useEffect(()=>{
//decode token
      if (token !== null) {
        const decodedTokenValue = decodeToken(token);
       console.log(JSON.stringify(decodedTokenValue));
      }
  },[]);

  //GET Template By Group & Company
    useEffect(() => {
      setTemplatesForDisplay();
      setTemplates();
      getTemplateByGroupIdCompanyId();
  },[groupId, companyId]);

  // Refresh the table
  const refreshTable = () => {
    // Placeholder for actual data refresh logic
    setTemplatesForDisplay();
    setTemplates();
    getTemplateByGroupIdCompanyId();
  };

  const getTemplateByGroupIdCompanyId = async () => { 
    if(localStorage.getItem('groupId') !== null && localStorage.getItem('companyId') !== null){
      await TemplatesAPI.getTemplateByGroupIdCompanyId(token, localStorage.getItem('groupId'), localStorage.getItem('companyId')).then((res) => {
        if (res?.data) {
            const rows = [];
            res.data.map((rowValue) =>  {
              let statusValue = null;
              if(rowValue.status){
                statusValue = "Active"
              }
              else{
                statusValue = "Inactive"
              }
              rows.push({_id: rowValue._id, templateName: rowValue.name,  templateType: rowValue.type, 
                uploadedDate: moment(rowValue.created_at).format('DD/MM/YYYY'), status: statusValue, fileUrl: rowValue.fileUrl, fileName: rowValue.file})
            });
            setTemplatesForDisplay(rows);
            setTemplates(res.data);
        }
    }).catch(err => {
        console.log("error getting template data :", err.message);
    })
    }else if (localStorage.getItem('groupId') !== null && localStorage.getItem('companyId') === null){
      setTemplatesForDisplay();
      setTemplates();
    }  
  };

  // Column search functionality
  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => handleReset(clearFilters, confirm)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase())
        : '',
    onFilterDropdownVisibleChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
  });
  
  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };
  
  const handleReset = (clearFilters, confirm) => {
    clearFilters();
    setSearchText('');
    setSearchedColumn('');
    confirm();
  };

  const initialColumns = [
    {
      title: "Template Name",
      dataIndex: "templateName",
      key: "templateName",
      ...getColumnSearchProps("Template Name"),
      percentageWidth: 0.30,
    },
    {
      title: "Template Type",
      dataIndex: "templateType",
      key: "templateType",
      sorter: (a, b) => a.templateType.localeCompare(b.templateType),
      percentageWidth: 0.20,
    },
    {
      title: "Uploaded Date",
      dataIndex: "uploadedDate",
      key: "uploadedDate",
      percentageWidth: 0.15,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      percentageWidth: 0.15,
      render: (status) => (
        <Badge
          status={status === "Active" ? "success" : "error"}
          text={status}
        />
      ),
    },
    {
      title: "Action",
      key: "action",
      percentageWidth: 0.10,
      align:"center",
      render: (_, record) => (
        <Space
          size="small"
          style={{ display: "flex", justifyContent: "space-around" }}
        >
          <Button
            icon={downloadingTemplateId === record._id ? <Spin size="small" /> : <DownloadOutlined />}
            size="small"
            loading={downloadingTemplateId === record._id}
            onClick={(e) => onViewChange(record._id, record.fileName)}
            disabled={downloadingTemplateId === record._id}
          />
          <Button
            icon={<EditOutlined />}
            size="small"
            onClick={(e) => onEditChange(record._id)}
          />
        </Space>
      ),
    },
  ];

  const onEditChange = (id) => {
    if (editRef.current) {
      editRef.id = id;
      editRef.current.openEditDrawer(); // Call the openDrawer method in Edit
    }
  }

  const onViewChange = async (templateId, fileName) => {
    setDownloadingTemplateId(templateId);
    try {
      // Use the new download API
      const response = await TemplatesAPI.downloadTemplateById(
        token, 
        localStorage.getItem('groupId'), 
        localStorage.getItem('companyId'), 
        templateId
      );
      
      // The response.data is already a blob when responseType: 'blob' is set
      if (response && response.data) {
        // Create a Blob URL from the response data
        const blobUrl = URL.createObjectURL(response.data);
        const updatedFileName = GetFileName(fileName);
        
        // Create download link
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = updatedFileName;
        console.log("Downloading file as:", updatedFileName);
        
        // Trigger download
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl); // Free up memory
        
        message.success('Template downloaded successfully!');
      } else {
        message.error('Failed to download template: No data received');
      }
    } catch (error) {
      console.error("Error downloading template:", error);
      message.error('Failed to download template. Please try again.');
    } finally {
      setDownloadingTemplateId(null);
    }
  }

  const GetFileName = (fileName)=>{
      let firstIndex = fileName.indexOf('_');
      let fileName_2  = fileName.substring(firstIndex +1);
        
      let lastIndex = fileName_2.lastIndexOf('.');
      let fileName_1 = fileName_2.substring(0, lastIndex);
      let fileType = fileName_2.substring(lastIndex + 1);

      let currentDate = new Date();
      let isoString = currentDate.toISOString();
      // Split at the "T" character to get the date part
      let formattedDate = isoString.split("T")[0];
      let FinalFileName = fileName_1+"_"+formattedDate+"."+fileType;
      //console.log(FinalFileName);
      return FinalFileName;
  }

  // Compute an initial container width using window.innerWidth as fallback.
  const initialContainerWidth = typeof window !== 'undefined' ? window.innerWidth : 1200;

  // Set initial column widths based on the fallback container width.
  const [columns, setColumns] = useState(
    initialColumns.map(col => ({
      ...col,
      width: Math.floor(initialContainerWidth * col.percentageWidth),
    }))
  );

  // Function to update column widths based on the measured container.
  const updateColumnWidths = useCallback(() => {
    if (tableContainerRef.current) {
      const containerWidth = tableContainerRef.current.offsetWidth;
      setColumns(initialColumns.map(col => ({
        ...col,
        width: Math.floor(containerWidth * col.percentageWidth),
      })));
    }
  }, [initialColumns]);

  // Use useLayoutEffect to update column widths as soon as the component is mounted.
  useLayoutEffect(() => {
    updateColumnWidths();
  }, [updateColumnWidths]);

  // Update widths on window resize.
  useEffect(() => {
    window.addEventListener('resize', updateColumnWidths);
    return () => window.removeEventListener('resize', updateColumnWidths);
  }, [updateColumnWidths]);


const handleResize = useCallback(
    (index) => (e, { size }) => {
      setColumns((prevColumns) => {
        const newColumns = [...prevColumns];
        newColumns[index] = { ...newColumns[index], width: size.width };
        return newColumns;
      });
    },
    []
  );
  
  const mergedColumns = useMemo(
    () =>
      columns.map((col, index) => ({
        ...col,
        onHeaderCell: (column) => ({
          width: column.width,
          onResize: handleResize(index),
        }),
      })),
    [columns, handleResize]
  );

  return (
    <Row>
      <Col md={24}>
        <Breadcrumb style={{ margin: "16px 0" }}>
          <Breadcrumb.Item href="/dashboard">
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            Templates
          </Breadcrumb.Item>
          <Breadcrumb.Item>Templates</Breadcrumb.Item>
        </Breadcrumb>
        <div className="border-container">
          <Button
            type="primary"
            variant="dashed"
            className="card-button"
            size="large"
            icon={<PlusOutlined />}
            onClick={showChildDrawer}
            shape="circle"
            disabled={localStorage.getItem('groupId') === null || localStorage.getItem('companyId') === null}
          ></Button>
          <Card title="Templates" bordered={true}>
          <div
              style={{
                maxWidth: '100%', // Ensure the container doesn't exceed its width
                overflowX: 'auto', // Enable horizontal scroll
              }}
            >
            <Table size="middle"  components={{
                header: {
                  cell: ResizableTitle, 
                },
              }}
              columns={mergedColumns} locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description='No records found!'/> }} dataSource={templatesForDisplay} />
              </div>
          </Card>
        </div>
        <Create ref={createRef} onRefresh={refreshTable}/>
        <Edit ref={editRef} onRefresh={refreshTable}/>
      </Col>
    </Row>
  );
}
